import React, { useState, useEffect } from 'react';
import { Steps, Button, Card, Row, Col, Checkbox, Input, Select, Divider, Space, Typography, Tag, Layout , message } from 'antd';
import { DatabaseOutlined, SettingOutlined, EyeOutlined, LayoutOutlined, SaveOutlined  } from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const { Step } = Steps;
const { Title, Text } = Typography;

export default function ReportWizard() {
    const [current, setCurrent] = useState(0);
    const [domains, setDomains] = useState({});
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [config, setConfig] = useState({
        domainKey: 'INVENTORY',
        title: '',
        selectedFields: [], // [{table: 'main', field: 'itemnum'}]
        groupBy: null,
        isLandscape: true,
        hasSuperset: false,
        supersetId: null,
        parameters: [
            { name: 'siteid', label: 'Site', type: 'dropdown', lookupType: 'sites' },
            { name: 'location', label: 'Storeroom', type: 'dropdown', lookupType: 'locations' }
        ]
    });

    useEffect(() => {
        axios.get('/api/reports/wizard/config').then(res => setDomains(res.data));
    }, []);

    const toggleField = (table, field) => {
        const fieldKey = `${table}.${field}`;
        const exists = config.selectedFields.find(f => f.id === fieldKey);
        if (exists) {
            setConfig({...config, selectedFields: config.selectedFields.filter(f => f.id !== fieldKey)});
        } else {
            setConfig({...config, selectedFields: [...config.selectedFields, {id: fieldKey, table, field}]});
        }
    };

    const handleGenerateReport = async () => {
        if (!config.title) {
            return message.error("Please enter a report title first!");
        }
        if (config.selectedFields.length === 0) {
            return message.error("Please select at least one field!");
        }
        const finalConfig = {
            ...config,
            parameters: [
                ...config.parameters,
                { name: 'group_by', type: 'hidden', default: config.groupBy }
            ]
        };
        try {
            setLoading(true); // تأكد من تعريف const [loading, setLoading] = useState(false)
            const token = localStorage.getItem('token');
            const response = await axios.post('/api/reports/wizard/generate', finalConfig, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            message.success({
                content: 'Report Template Generated Successfully! 🚀',
                duration: 3
            });

            // 🎯 التحويل لصفحة الديزاينر فوراً لتعديل اللمسات الأخيرة
            setTimeout(() => {
                navigate(`/reportdesigner/${response.data.id}`);
            }, 1500);

        } catch (error) {
            console.error("Wizard Error:", error);
            message.error("Failed to generate report: " + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };
    const steps = [
        { 
            title: 'Select Domain', 
            icon: <DatabaseOutlined />,
            content: (
                <Row gutter={[16, 16]}>
                    {Object.entries(domains).map(([key, d]) => (
                        <Col span={8} key={key}>
                            <Card 
                                hoverable 
                                className={config.domainKey === key ? 'border-primary bg-blue-50' : ''}
                                onClick={() => setConfig({...config, domainKey: key, selectedFields: []})}
                            >
                                <Title level={4}>{d.label}</Title>
                                <Tag color="blue">{d.main_table.toUpperCase()}</Tag>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )
        },
        {
            title: 'Pick Fields',
            icon: <LayoutOutlined />,
            content: (
                <div className="fields-selector">
                    <Divider orientation="left">Main Table Fields ({domains[config.domainKey]?.main_table})</Divider>
                    <Row>
                        {domains[config.domainKey]?.primary_fields.map(f => (
                            <Col span={6} key={f}>
                                <Checkbox onChange={() => toggleField('main', f)}>{f.toUpperCase()}</Checkbox>
                            </Col>
                        ))}
                    </Row>
                    
                    {domains[config.domainKey]?.related.map(rel => (
                        <div key={rel.name}>
                            <Divider orientation="left">Related: {rel.label}</Divider>
                            <Row>
                                {rel.fields.map(f => (
                                    <Col span={6} key={f}>
                                        <Checkbox onChange={() => toggleField(rel.name, f)}>{f.toUpperCase()}</Checkbox>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    ))}
                </div>
            )
        },
        {
            title: 'Finalize & Charts',
            icon: <SettingOutlined />,
            content: (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <Card title="Report Information" borderless className="bg-gray-50">
                        <Input 
                            size="large" 
                            prefix={<DatabaseOutlined />}
                            placeholder="Enter Report Name (e.g. Monthly Stock Balance)" 
                            value={config.title}
                            onChange={e => setConfig({...config, title: e.target.value})} 
                        />
                        <div style={{ marginTop: 15 }}>
                            <Checkbox 
                                checked={config.isLandscape} 
                                onChange={e => setConfig({...config, isLandscape: e.target.checked})}
                            >
                                <b>Landscape Mode</b> (Recommended for many columns)
                            </Checkbox>
                        </div>
                    </Card>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Card title="Grouping Logic" size="small">
                                <Text type="secondary">Organize records by a specific field:</Text>
                                <Select 
                                    placeholder="Select field to group by..." 
                                    style={{width: '100%', marginTop: 10}} 
                                    allowClear
                                    onChange={val => setConfig({...config, groupBy: val})}
                                >
                                    {config.selectedFields.map(f => (
                                        <Select.Option key={f.id} value={f.table === 'main' ? f.field : `${f.table}_ref.${f.field}`}>
                                            {f.field.toUpperCase()} ({f.table})
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card title="Visual Analytics" size="small">
                                <Checkbox 
                                    checked={config.hasSuperset}
                                    onChange={e => setConfig({...config, hasSuperset: e.target.checked})}
                                >
                                    Embed Superset Dashboard
                                </Checkbox>
                                {config.hasSuperset && (
                                    <Input 
                                        className="mt-2" 
                                        placeholder="Paste Dashboard UUID/ID here" 
                                        onChange={e => setConfig({...config, supersetId: e.target.value})}
                                    />
                                )}
                            </Card>
                        </Col>
                    </Row>
                </Space>
            )
        }
    ];

    return (
        <Layout style={{ padding: '24px', background: '#fff', minHeight: '100vh' }}>
            <Title level={2}><SettingOutlined /> Report Engine Wizard</Title>
            <Steps current={current} style={{ marginBottom: 40 }}>
                {steps.map(s => <Step key={s.title} title={s.title} icon={s.icon} />)}
            </Steps>

            <div className="wizard-step-content" style={{ padding: '40px', background: '#fafafa', border: '1px dashed #d9d9d9' }}>
                {steps[current].content}
            </div>

            <div style={{ marginTop: 24, textAlign: 'right' }}>
                {current > 0 && <Button style={{ marginRight: 8 }} onClick={() => setCurrent(current - 1)}>Back</Button>}
                {current < steps.length - 1 && <Button type="primary" onClick={() => setCurrent(current + 1)}>Next Step</Button>}
                {current === steps.length - 1 && (
                    <Button 
                        type="primary" 
                        size="large" 
                        loading={loading}
                        icon={<SaveOutlined />} 
                        onClick={handleGenerateReport} // 🎯 ربط الدالة هنا
                    >
                        Generate & Save Template 🚀
                    </Button>
                )}
            </div>
        </Layout>
    );
}